// SolarInstallationMaintenanceService.js - Detailed Solar System Maintenance service component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a detailed Solar System Maintenance service section
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the service section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The Solar Maintenance service section elements
 */
export const createSolarInstallationMaintenanceServiceElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const maintenanceSectionClass = 'maintenance-service-section';
  const maintenanceContainerClass = 'maintenance-service-container';
  const maintenanceHeaderClass = 'maintenance-service-header';
  const maintenanceTitleClass = 'maintenance-service-title';
  const maintenanceSubtitleClass = 'maintenance-service-subtitle';
  const maintenanceContentClass = 'maintenance-service-content';
  const maintenanceHeroClass = 'maintenance-service-hero';
  const maintenanceHeroImageClass = 'maintenance-service-hero-image';
  const maintenanceHeroTextClass = 'maintenance-service-hero-text';
  const maintenanceFeaturesClass = 'maintenance-service-features';
  const maintenanceFeatureCardClass = 'maintenance-service-feature-card';
  const maintenanceFeatureIconClass = 'maintenance-service-feature-icon';
  const maintenanceFeatureTitleClass = 'maintenance-service-feature-title';
  const maintenanceFeatureDescClass = 'maintenance-service-feature-desc';
  const maintenancePackagesClass = 'maintenance-service-packages';
  const maintenancePackageCardClass = 'maintenance-service-package-card';
  const maintenancePackageTitleClass = 'maintenance-service-package-title';
  const maintenancePackagePriceClass = 'maintenance-service-package-price';
  const maintenancePackageListClass = 'maintenance-service-package-list';
  const maintenancePackageItemClass = 'maintenance-service-package-item';
  const maintenanceCtaClass = 'maintenance-service-cta';
  const maintenanceCtaButtonClass = 'maintenance-service-cta-button';
  
  // Desktop Styles - Solar themed with deep blue background
  const maintenanceSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#0f2341',
    background: `
      radial-gradient(circle at 20% 80%, rgba(74, 222, 128, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #0f2341 0%, #1e3a8a 50%, #0f2341 100%)
    `,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const maintenanceContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '60px'
  };

  const maintenanceHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%'
  };

  const maintenanceTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const maintenanceSubtitleStyles = {
    fontSize: '20px',
    color: '#e2e8f0',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400',
    maxWidth: '700px'
  };

  const maintenanceContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '80px'
  };

  const maintenanceHeroStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '50px',
    width: '100%'
  };

  const maintenanceHeroImageStyles = {
    flex: '1',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
  };

  const maintenanceHeroTextStyles = {
    flex: '1',
    color: '#ffffff',
    fontSize: '18px',
    lineHeight: '1.8',
    fontFamily: '"Inter", sans-serif'
  };

  const maintenanceFeaturesStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '30px',
    width: '100%'
  };

  const maintenanceFeatureCardStyles = {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    padding: '30px',
    border: '1px solid rgba(74, 222, 128, 0.2)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease'
  };

  const maintenanceFeatureIconStyles = {
    fontSize: '48px',
    marginBottom: '20px',
    display: 'block'
  };

  const maintenanceFeatureTitleStyles = {
    fontSize: '24px',
    fontWeight: '600',
    color: '#4ade80',
    marginBottom: '15px',
    fontFamily: '"Inter", sans-serif'
  };

  const maintenanceFeatureDescStyles = {
    fontSize: '16px',
    color: '#e2e8f0',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif'
  };

  const maintenancePackagesStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
    width: '100%'
  };

  const maintenancePackageCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '40px',
    border: '2px solid #4ade80',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease'
  };

  const maintenancePackageTitleStyles = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#0f2341',
    marginBottom: '10px',
    fontFamily: '"Inter", sans-serif'
  };

  const maintenancePackagePriceStyles = {
    fontSize: '36px',
    fontWeight: '700',
    color: '#4ade80',
    marginBottom: '25px',
    fontFamily: '"Inter", sans-serif'
  };

  const maintenancePackageListStyles = {
    listStyle: 'none',
    padding: '0',
    margin: '0'
  };

  const maintenancePackageItemStyles = {
    fontSize: '16px',
    color: '#475569',
    marginBottom: '12px',
    paddingLeft: '25px',
    position: 'relative',
    fontFamily: '"Inter", sans-serif'
  };

  const maintenanceCtaStyles = {
    textAlign: 'center',
    width: '100%'
  };

  const maintenanceCtaButtonStyles = {
    padding: '18px 40px',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: '#4ade80',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: '"Inter", sans-serif',
    boxShadow: '0 4px 15px rgba(74, 222, 128, 0.3)'
  };

  // Mobile styles
  const mobileMaintenanceHeroStyles = {
    flexDirection: 'column',
    gap: '30px'
  };

  const mobileMaintenanceTitleStyles = {
    fontSize: '36px'
  };

  const mobileMaintenancePackageCardStyles = {
    padding: '30px 20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(maintenanceSectionClass, maintenanceSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceContainerClass, maintenanceContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceHeaderClass, maintenanceHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceTitleClass, maintenanceTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceSubtitleClass, maintenanceSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceContentClass, maintenanceContentStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceHeroClass, maintenanceHeroStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceHeroImageClass, maintenanceHeroImageStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceHeroTextClass, maintenanceHeroTextStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceFeaturesClass, maintenanceFeaturesStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceFeatureCardClass, maintenanceFeatureCardStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceFeatureIconClass, maintenanceFeatureIconStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceFeatureTitleClass, maintenanceFeatureTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceFeatureDescClass, maintenanceFeatureDescStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenancePackagesClass, maintenancePackagesStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenancePackageCardClass, maintenancePackageCardStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenancePackageTitleClass, maintenancePackageTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenancePackagePriceClass, maintenancePackagePriceStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenancePackageListClass, maintenancePackageListStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenancePackageItemClass, maintenancePackageItemStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceCtaClass, maintenanceCtaStyles);
    newRegistry[DEVICES.DESKTOP].set(maintenanceCtaButtonClass, maintenanceCtaButtonStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(maintenanceHeroClass, mobileMaintenanceHeroStyles);
    newRegistry[DEVICES.MOBILE].set(maintenanceTitleClass, mobileMaintenanceTitleStyles);
    newRegistry[DEVICES.MOBILE].set(maintenancePackageCardClass, mobileMaintenancePackageCardStyles);

    return newRegistry;
  });
  
  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    maintenanceSectionClass,
    maintenanceContainerClass,
    maintenanceHeaderClass,
    maintenanceTitleClass,
    maintenanceSubtitleClass,
    maintenanceContentClass,
    maintenanceHeroClass,
    maintenanceHeroImageClass,
    maintenanceHeroTextClass,
    maintenanceFeaturesClass,
    maintenanceFeatureCardClass,
    maintenanceFeatureIconClass,
    maintenanceFeatureTitleClass,
    maintenanceFeatureDescClass,
    maintenancePackagesClass,
    maintenancePackageCardClass,
    maintenancePackageTitleClass,
    maintenancePackagePriceClass,
    maintenancePackageListClass,
    maintenancePackageItemClass,
    maintenanceCtaClass,
    maintenanceCtaButtonClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  const contentId = sectionId + 5;
  const heroId = sectionId + 6;
  const heroImageId = sectionId + 7;
  const heroTextId = sectionId + 8;
  const featuresId = sectionId + 9;
  const packagesId = sectionId + 20;
  const ctaId = sectionId + 30;
  const ctaButtonId = sectionId + 31;

  const newElements = [
    // Main Maintenance Service Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Maintenance Service Section',
      parentId: null,
      className: maintenanceSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'maintenance-service-section',
        'data-component': 'section',
        'data-section-type': 'service',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Maintenance Service Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: maintenanceContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'maintenance-service-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Maintenance Service Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: maintenanceHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'maintenance-service-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Maintenance Service Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h1'),
      className: maintenanceTitleClass,
      styles: {},
      children: [],
      text: 'Solar System Maintenance',
      attributes: {
        'data-element': 'maintenance-service-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Maintenance Service Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: maintenanceSubtitleClass,
      styles: {},
      children: [],
      text: 'Comprehensive solar system maintenance services to maximize performance and extend system lifespan. Professional cleaning, monitoring, repairs, and preventive care to ensure optimal energy production.',
      attributes: {
        'data-element': 'maintenance-service-subtitle'
      }
    },
    
    // Content Container
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Maintenance Service Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: maintenanceContentClass,
      styles: {},
      children: [heroId, featuresId, packagesId, ctaId],
      text: '',
      attributes: {
        'data-element': 'maintenance-service-content'
      }
    },
    
    // Hero Section
    {
      id: heroId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Maintenance Service Hero',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: maintenanceHeroClass,
      styles: {},
      children: [heroImageId, heroTextId],
      text: '',
      attributes: {
        'data-element': 'maintenance-service-hero'
      }
    },
    
    // Hero Image
    {
      id: heroImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Maintenance Service Hero Image',
      parentId: heroId,
      hierarchyName: getHierarchyName('image'),
      className: maintenanceHeroImageClass,
      styles: { width: '100%', height: 'auto' },
      src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      children: [],
      text: '',
      attributes: {
        'data-element': 'maintenance-service-hero-image',
        'alt': 'Professional solar panel cleaning and maintenance'
      }
    },
    
    // Hero Text
    {
      id: heroTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Maintenance Service Hero Text',
      parentId: heroId,
      hierarchyName: getHierarchyName('div'),
      className: maintenanceHeroTextClass,
      styles: {},
      children: [],
      text: 'JNT & Co. provides comprehensive solar system maintenance services to ensure your investment continues to deliver maximum returns. Our expert maintenance team offers regular cleaning, performance monitoring, preventive inspections, and prompt repairs to keep your solar system operating at peak efficiency. We understand that proper maintenance is crucial for maximizing energy production, extending system lifespan, and protecting your warranty. Our services include panel cleaning, inverter maintenance, electrical connection inspections, and detailed performance analysis to identify and resolve issues before they impact your energy savings.',
      attributes: {
        'data-element': 'maintenance-service-hero-text'
      }
    }
  ];

  return {
    maintenanceServiceSection: newElements[0],
    elements: newElements
  };
}; 